import { BASE_URL } from "#next-seo.config";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import { useEffect, useState } from "react";
import Container from "~/components/Container";
import { CONTACT_LIMITS, contactErrors, type ContactMessage } from "~/lib/contact";

export const relativeUrl = `/contact`;
export const url = `${BASE_URL}${relativeUrl}`;
export const title = "Contact Samuel Batista";
export const description = "Send Samuel Batista a message about a project, a question or a post on this site.";

type Errors = Partial<Record<keyof ContactMessage | "captcha", string>>;

// A sum to keep simple bots out, asked and checked in the browser, as on DataConnector-Pro's quote form.
type Captcha = { left: number; right: number; answer: number };

function newCaptcha(): Captcha {
  const left = Math.floor(Math.random() * 8) + 2;
  const right = Math.floor(Math.random() * 8) + 2;
  return { left, right, answer: left + right };
}

const label = "mb-1.5 block text-sm font-medium text-gray-900 dark:text-gray-100";
const field =
  "block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500";

function FieldError({ children }: { children?: string }) {
  return children ? <p className="mt-1 text-sm text-red-700 dark:text-red-400">{children}</p> : null;
}

export default function Contact() {
  const [values, setValues] = useState<ContactMessage>({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState<Errors>({});
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [failure, setFailure] = useState("");
  // Fixed until mount, so the server's HTML and the first render agree; then a random one.
  const [captcha, setCaptcha] = useState<Captcha>({ left: 4, right: 5, answer: 9 });
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const refreshCaptcha = () => {
    setCaptcha(newCaptcha());
    setCaptchaAnswer("");
    setErrors((prev) => ({ ...prev, captcha: undefined }));
  };
  useEffect(refreshCaptcha, []);

  const complete = captchaAnswer !== "" && Object.values(values).every((value) => value.trim() !== "");

  const set = (key: keyof ContactMessage) => (e: { target: { value: string } }) => {
    setValues({ ...values, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const send = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const found: Errors = contactErrors(values);
    if (Number(captchaAnswer) !== captcha.answer) {
      found.captcha = "That's not the answer. Try again, or ask for a new question.";
    }
    setErrors(found);
    if (Object.keys(found).length) return;

    setState("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });
      const { error } = await res.json();
      if (error) {
        setFailure(error);
        setState("failed");
        return;
      }
      setValues({ name: "", email: "", message: "" });
      refreshCaptcha();
      setState("sent");
    } catch {
      setFailure("Couldn't reach the server. Please try again.");
      setState("failed");
    }
  };

  return (
    <Container>
      <Head>
        {generateNextSeo({
          title,
          description,
          canonical: url,
          openGraph: {
            url,
            title,
            description
          }
        })}
      </Head>
      <div className="flex flex-col justify-center items-start max-w-2xl mx-auto mb-16">
        <h1 className="font-bold text-3xl md:text-5xl tracking-tight mb-4 text-black dark:text-white">Contact</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Have a project in mind, a question about one of my posts, or something to tell me?
        </p>
        <div className="w-full rounded-lg border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-gray-900">
          {state === "sent" ? (
            <div role="status" className="text-center">
              <p className="font-bold text-gray-900 dark:text-gray-100">Thanks, your message was received.</p>
              <button
                type="button"
                onClick={() => setState("idle")}
                className="mt-4 text-sm font-medium text-blue-600 shadow-none hover:underline !bg-transparent hover:!bg-transparent dark:text-blue-400 dark:!bg-transparent dark:hover:!bg-transparent"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={send} noValidate className="space-y-5">
              <div>
                <label htmlFor="contact-name" className={label}>
                  Name
                </label>
                <input
                  id="contact-name"
                  name="name"
                  autoComplete="name"
                  placeholder="Your name"
                  maxLength={CONTACT_LIMITS.name}
                  value={values.name}
                  onChange={set("name")}
                  aria-invalid={Boolean(errors.name)}
                  className={field}
                />
                <FieldError>{errors.name}</FieldError>
              </div>
              <div>
                <label htmlFor="contact-email" className={label}>
                  Email
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  maxLength={CONTACT_LIMITS.email}
                  value={values.email}
                  onChange={set("email")}
                  aria-invalid={Boolean(errors.email)}
                  className={field}
                />
                <FieldError>{errors.email}</FieldError>
              </div>
              <div>
                <label htmlFor="contact-message" className={label}>
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={6}
                  placeholder="What's on your mind?"
                  maxLength={CONTACT_LIMITS.message}
                  value={values.message}
                  onChange={set("message")}
                  aria-invalid={Boolean(errors.message)}
                  className={`${field} min-h-32`}
                />
                <FieldError>{errors.message}</FieldError>
              </div>
              <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-950">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="contact-captcha" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Verify you&apos;re a human:{" "}
                    <span className="whitespace-nowrap">
                      {captcha.left} + {captcha.right} =
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="shrink-0 text-sm font-medium text-blue-600 shadow-none hover:underline !bg-transparent hover:!bg-transparent dark:text-blue-400 dark:!bg-transparent dark:hover:!bg-transparent"
                  >
                    New question
                  </button>
                </div>
                <input
                  id="contact-captcha"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Answer"
                  maxLength={3}
                  value={captchaAnswer}
                  onChange={(e) => {
                    setCaptchaAnswer(e.target.value.replace(/\D/g, ""));
                    if (errors.captcha) setErrors({ ...errors, captcha: undefined });
                  }}
                  aria-invalid={Boolean(errors.captcha)}
                  className={field}
                />
                <FieldError>{errors.captcha}</FieldError>
              </div>
              <button
                type="submit"
                disabled={!complete || state === "sending"}
                className="h-11 w-full rounded-md text-base font-semibold text-white shadow-none !bg-blue-600 enabled:hover:!bg-blue-700 dark:!bg-blue-600 dark:enabled:hover:!bg-blue-500"
              >
                {state === "sending" ? "Sending…" : "Send message"}
              </button>
              {state === "failed" && (
                <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                  {failure}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </Container>
  );
}
